import React, { useEffect, useState } from "react";
import { Redirect, useParams } from "react-router-dom";
import { connect } from "react-redux";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Typography,
} from "@material-ui/core";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import {
  resetPasswordRequest,
  validateTokenRequest,
} from "../../AuthApiActions";
import logo from "../../../../assets/logo.svg";
import { getUserInfo, isLoggedIn } from "../../../../utilities/storage";
import UtilHelper from "../../../../utilities/validators/UtilHelper";
import {
  APP_URLS,
  dxAIAdminMenu,
  orgAdminMenu,
  ROLES,
  superAdminMenu,
  userMenu,
  userMenuDoctor,
} from "../../../../utilities/constants";
import "./ResetPassword.css";
import ErrorPage from "../../../../utilities/pages/ErrorPage/ErrorPage";

import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

/**
 * function to render reset password form
 * @param history: history from router
 * @param resetPassword: function to reset password
 * @returns {JSX.Element}
 * @constructor
 */
const ResetPassword = ({ history, resetPassword, validateToken }) => {
  const params = useParams();
  const role = getUserInfo() ? getUserInfo().Roles[0].name : "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    ValidatorForm.addValidationRule("isValidPassword", (value) =>
      UtilHelper.validatePassword(value)
    );
    ValidatorForm.addValidationRule("isPasswordMatch", (value) =>
      UtilHelper.matchPassword(value, password)
    );

    return () => {
      ValidatorForm.removeValidationRule("isValidPassword");
      ValidatorForm.removeValidationRule("isPasswordMatch");
    };
  }, [password]);

  useEffect(() => {
    validateToken(params.id, params.token)
      .then((res) => {
        setLoading(false);
      })
      .catch((error) => {
        setTokenError({
          status: error.response.status,
          message: error.response.data.error,
        });
      });
  }, [validateToken, params.id, params.token]);

  /**
   * function to handle forgot password on button click
   * @param e: event from button click
   */
  const onSubmit = (e) => {
    e.preventDefault();

    const data = {
      password: password,
    };

    resetPassword(params.id, params.token, data).then(() => {
      history.push(APP_URLS.LOGIN);
    });
  };

  const getRouteAfterLogin = (role) => {
    let url = "";
    switch (role) {
      case ROLES.ROLE_SUPER_ADMIN:
        url = dxAIAdminMenu[0].path;
        break;
      case ROLES.ROLE_SYSTEM_ADMIN:
        url = superAdminMenu[0].path;
        break;
      case ROLES.ROLE_CLINIC_OWNER:
        url = superAdminMenu[0].path;
        break;
      case ROLES.ROLE_ORG_ADMIN:
        url = orgAdminMenu[0].path;
        break;
      case ROLES.ROLE_DOCTOR:
        url = userMenuDoctor[0].path;
        break;
      case ROLES.ROLE_RECEPTIONIST:
      case ROLES.ROLE_PHARMACIST:
      case ROLES.ROLE_THERAPIST:
        url = userMenu[0].path;
        break;
      default:
        break;
    }

    return url;
  };

  if (isLoggedIn()) {
    if (role === ROLES.ROLE_SYSTEM_ADMIN) {
      return <Redirect to={APP_URLS.ORGANIZATIONS} />;
    } else {
      const redirectUrlBasedOnRole = getRouteAfterLogin(role);
      if (redirectUrlBasedOnRole) {
        return <Redirect to={redirectUrlBasedOnRole} />;
      }
    }
  }

  return tokenError ? (
    <ErrorPage code={tokenError.status} message={tokenError.message} />
  ) : (
    !loading && (
      <Container component="main" maxWidth="xs">
        <CssBaseline />

        <Box className="forget-password-form-container">
          <img height={150} src={logo} alt="logo" />
          <Typography
            component="h1"
            variant="h5"
            style={{ marginTop: "1.5rem" }}
          >
            Reset Password
          </Typography>

          <ValidatorForm
            noValidate
            className="forget-password-form"
            onSubmit={onSubmit}
          >
            <TextValidator
              variant="outlined"
              margin="normal"
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              validators={["required", "isValidPassword"]}
              errorMessages={[
                "Password is required",
                "Password should have minimum 1 uppercase, 1 lowercase, 1 numeric, 1 special characters",
              ]}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextValidator
              variant="outlined"
              margin="normal"
              fullWidth
              name="password"
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              validators={["isPasswordMatch"]}
              errorMessages={["Passwords does not match "]}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className="forget-password-button"
            >
              Set Password
            </Button>
          </ValidatorForm>
        </Box>
      </Container>
    )
  );
};

const mapStateToProps = (state) => {
  return {
    loggedInUser: state.auth.loggedInUser,
  };
};

const mapDispatchToProp = (dispatch) => {
  return {
    resetPassword: (id, token, data) => {
      return dispatch(resetPasswordRequest(id, token, data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    validateToken: (id, token) => {
      return dispatch(validateTokenRequest(id, token)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProp)(ResetPassword);
